import { inject, Injectable } from '@angular/core';
import { InjectableSuperclass } from '@sama/ng-core';
import { isEqual } from 'lodash';
import { DebouncedEventTracker } from '../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { buildTrackerFromShellConnector } from '../../../cross-team-shared/event-tracking/event-utils';
import {
  MouseEventService,
  WheelEventInfo,
} from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { EventTrackingValueWithSource } from '../../../cross-team-shared/misc-utils';
import {
  dividePoints,
  multiplyPoints,
  scalePoint,
  subtractPoints,
} from '../../../shared/misc-utilities/point-utils';
import { ViewBox } from '../../../team-apis/content/view-box';
import { PanSource } from '../../../team-apis/core/pan-source';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { MouseCoordinateService } from '../mouse-coordinate.service';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import { ZOOM_PANEL_FACTOR } from '../tools/zoom/zoom-tool';
import { ViewBoxBuilderService } from './view-box-builder.service';
import { ViewBoxClampingService } from './view-box-clamping.service';
import { ViewBoxPaddingService } from './view-box-padding.service';

export const ZOOM_FACTOR = 1.1;

/**
 * Hacky padding for the pan in tests. Without this, the buttons are over the
 * canvas in tests and a few hundred tests fail.
 */
export const HACKY_VIEW_BOX_PADDING = 4;

/**
 * The pixelSize * MAX_ZOOM_IN_SIZE_MULTIPLIER provides the number of pixels
 * visible on the screen (vertically or horizontally, whichever is smaller)
 * when at max zoom.
 */
export const MIN_ZOOM_SIZE_MULTIPLIER = 10;
/**
 * This is the number of images that can fit on the screen when at min zoom.
 * Either vertically or horizontally, whichever is larger.
 */
export const MAX_ZOOM_SIZE_MULTIPLIER = 3;

@Injectable()
export class ViewBoxService extends InjectableSuperclass {
  private shellConnector = inject(shellConnectorToken);
  private store = inject(AbstractSensorContentStore2D);
  private mouseCoordinateService = inject(MouseCoordinateService);
  private viewBoxPaddingService = inject(ViewBoxPaddingService);
  private viewBoxBuilderService = inject(ViewBoxBuilderService);
  private viewBoxClampingService = inject(ViewBoxClampingService);
  private mouseEventService = inject(MouseEventService);

  private eventTracker: DebouncedEventTracker;
  private lastPanSourceUsed?: PanSource;

  constructor() {
    super();

    this.eventTracker = new DebouncedEventTracker(
      { id: 'zoom', bucket: 'navigation', sub_bucket: 'frame' },
      buildTrackerFromShellConnector(this.shellConnector),
    );
    this.subscribeTo(
      this.mouseEventService.getEvent$('container', 'wheel'),
      this.zoomWithWheel,
    );
  }

  getLastPanSourceUsed() {
    return this.lastPanSourceUsed;
  }

  /** Pan so that the given point is in the center, without changing zoom. */
  async centerToPoint(newCenter: Point2D, lastPanSourceUsed: PanSource) {
    const oldCenter =
      await this.mouseCoordinateService.getImagePointInCenterOfSvg();
    const vectorToCenter = subtractPoints(newCenter, oldCenter);
    this.panWithOffset(vectorToCenter, lastPanSourceUsed);
  }

  /**
   * This method takes an image point and zooms in or out with that point as
   * the center.
   */
  async zoom(direction: 'in' | 'out', source: string, imagePoint: Point2D) {
    const viewBoxState = this.store.state().viewBox;
    const pan: Point2D = [viewBoxState[0], viewBoxState[1]];
    const dimensions: Point2D = [viewBoxState[2], viewBoxState[3]];
    const factor = direction === 'out' ? ZOOM_FACTOR : 1 / ZOOM_FACTOR;

    const newDimensions = scalePoint(dimensions, factor);
    // We need to get the amount of change in the viewBox's dimensions to
    // calculate the new pan values
    const dimensionsDelta = subtractPoints(dimensions, newDimensions);

    // Getting the coordinate of the zoom's center point in the viewBox
    const panDirection = subtractPoints(imagePoint, pan);

    // This pan vector unit will be used to adjust the pan based on the
    // position of the zoom's target point (panDirection).
    const panDirectionUnit = dividePoints(panDirection, dimensions);
    const panDelta = multiplyPoints(dimensionsDelta, panDirectionUnit);

    const viewBoxToClamp: ViewBox = [
      viewBoxState[0] + panDelta[0],
      viewBoxState[1] + panDelta[1],
      newDimensions[0],
      newDimensions[1],
    ];
    const newViewBox =
      this.lastPanSourceUsed === 'inspect'
        ? await this.viewBoxClampingService.clampViewBoxWithPadding(
            viewBoxToClamp,
          )
        : this.viewBoxClampingService.clampViewBox(viewBoxToClamp);

    this.updateViewBoxAndTrack(newViewBox, { source });
  }

  async zoomToCenter(direction: 'in' | 'out', source: string) {
    const center =
      await this.mouseCoordinateService.getImagePointInCenterOfSvg();
    await this.zoom(direction, source, center);

    if (this.lastPanSourceUsed === 'inspect') {
      this.viewBoxPaddingService.updatePadding();
    }
  }

  async zoomToBox(corners: [Point2D, Point2D], source: PanSource) {
    this.lastPanSourceUsed = source;
    const newViewBox =
      await this.viewBoxBuilderService.buildFromBoundingPoints(corners);

    this.updateViewBoxAndTrack(
      await this.viewBoxClampingService.clampViewBoxWithPadding(newViewBox),
      { source },
    );
  }

  resetZoom(eventTrackingValue?: EventTrackingValueWithSource) {
    this.lastPanSourceUsed = undefined;

    this.updateViewBoxAndTrack(
      this.store.state().initialViewBox,
      eventTrackingValue,
    );
  }

  panWithOffset([offsetX, offsetY]: Point2D, lastPanSourceUsed: PanSource) {
    this.lastPanSourceUsed = lastPanSourceUsed;
    const viewBox = this.store.state().viewBox;

    this.updateViewBoxAndPropagate([
      viewBox[0] + offsetX,
      viewBox[1] + offsetY,
      viewBox[2],
      viewBox[3],
    ]);
  }

  initializeViewBox(viewBox: ViewBox, hackyPadding = 0) {
    this.store('initialViewBox').set(viewBox);
    // Temporary hack for tests. Without this, the buttons are over the
    // canvas in tests and a few hundred tests fail.
    this.updateViewBoxAndPropagate([
      viewBox[0] - hackyPadding,
      viewBox[1] - hackyPadding,
      viewBox[2] + hackyPadding,
      viewBox[3] + hackyPadding,
    ]);
  }

  updateViewBoxAndPropagate(viewBox: ViewBox) {
    this.store('viewBox').set(viewBox);
    this.shellConnector.propagateViewBoxUpdate(
      this.store.state().output.name,
      viewBox,
    );
  }

  updateViewBoxAndTrack(
    newViewBox: ViewBox,
    eventTrackingValue?: EventTrackingValueWithSource,
  ) {
    if (isEqual(newViewBox, this.store.state().viewBox)) {
      return;
    }

    this.updateViewBoxAndPropagate(newViewBox);
    this.mouseCoordinateService.handleViewBoxUpdate();

    if (!eventTrackingValue) {
      // Updating as part of another action, not a direct user action.
      return;
    }

    const isFirstTime = !this.eventTracker.isDebouncePending();
    if (isFirstTime) {
      this.eventTracker.startEvent();
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      if (eventTrackingValue) {
        this.eventTracker.addContext(eventTrackingValue);
      }
    }
    const state = this.store.state();
    const zoom = state.sensorRendererState.width / state.viewBox[2];
    this.eventTracker.trackWithDebounce({
      zoom,
      zoom_percentage: this.getZoomPercentage(zoom),
      ...eventTrackingValue,
    });
  }

  private getZoomPercentage(zoom: number) {
    const state = this.store.state();
    const maxZoomSize = MIN_ZOOM_SIZE_MULTIPLIER * state.pixelSize;
    const maxZoom =
      state.viewBox[2] / state.viewBox[3] <= 1
        ? state.sensorRendererState.width / maxZoomSize
        : state.sensorRendererState.height / maxZoomSize;
    return (zoom - 1) / (maxZoom - 1);
  }

  private async zoomWithWheel({ event }: WheelEventInfo) {
    event.preventDefault();

    const direction = event.deltaY > 0 ? 'out' : 'in';

    if (this.store.state().isZoomPanelOpen) {
      this.store('layerSettings')('zoomPanelScale').setUsing((scale) => {
        const factor =
          direction === 'in' ? ZOOM_PANEL_FACTOR : 1 / ZOOM_PANEL_FACTOR;
        return scale * factor;
      });
    } else {
      await this.zoomToCenter(direction, 'mouse_wheel');
    }
  }
}
